import { useState } from 'react'

import { Card } from '@/components/ui'
import { api } from '@/lib/api'
import type { BrokerAccountOption, CTraderStatus, TradingAccountAdmin } from '@/lib/types'

/**
 * Comptes de trading et raccordement cTrader — réservé à l'administration.
 *
 * Deux façons d'amener un compte dans l'application :
 *
 * - **Capital.com** : choisi dans la liste des comptes de la clé API du
 *   serveur, jamais recopié à la main — ses identifiants font dix-huit
 *   chiffres, et une faute de frappe rattacherait un compte inexistant ;
 * - **cTrader** — FTMO et les autres prop firms : le titulaire autorise
 *   l'application sur la page officielle de cTrader, et chaque compte de son
 *   cTrader ID arrive ici, nommé d'après son courtier.
 */
export function TradingAccountsAdmin({
  accounts,
  brokerAccounts,
  ctrader,
  onChanged,
  onError,
  onNotice,
}: {
  accounts: TradingAccountAdmin[]
  brokerAccounts: BrokerAccountOption[]
  ctrader: CTraderStatus | null
  onChanged: () => void
  onError: (message: string) => void
  onNotice: (message: string) => void
}) {
  return (
    <>
      <AccountsCard
        accounts={accounts}
        brokerAccounts={brokerAccounts}
        onChanged={onChanged}
        onError={onError}
        onNotice={onNotice}
      />
      {ctrader && (
        <CTraderCard
          status={ctrader}
          accounts={accounts}
          onChanged={onChanged}
          onError={onError}
          onNotice={onNotice}
        />
      )}
    </>
  )
}

// --------------------------------------------------------------------------- //
// Comptes
// --------------------------------------------------------------------------- //
function AccountsCard({
  accounts,
  brokerAccounts,
  onChanged,
  onError,
  onNotice,
}: {
  accounts: TradingAccountAdmin[]
  brokerAccounts: BrokerAccountOption[]
  onChanged: () => void
  onError: (message: string) => void
  onNotice: (message: string) => void
}) {
  const [adding, setAdding] = useState('')
  const [label, setLabel] = useState('')
  const [busy, setBusy] = useState(false)

  // On ne propose que les comptes Capital.com pas encore ajoutés.
  const known = new Set(
    accounts.filter((a) => a.provider === 'capital').map((a) => a.external_id),
  )
  const addable = brokerAccounts.filter((option) => !known.has(option.broker_account_id))

  async function add() {
    if (!adding || busy) return
    setBusy(true)
    try {
      await api.createTradingAccount({ external_id: adding, label })
      onNotice('Compte Capital.com ajouté. Ouvrez-le aux traders concernés.')
      setAdding('')
      setLabel('')
      onChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Ajout refusé')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={`Comptes de trading (${accounts.length})`}>
      {accounts.length === 0 ? (
        <p className="px-3 py-6 text-center text-[13px] text-ink-muted">
          Aucun compte de trading pour l'instant.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {accounts.map((account) => (
            <AccountRow
              key={account.id}
              account={account}
              onChanged={onChanged}
              onError={onError}
              onNotice={onNotice}
            />
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-2 border-t border-border p-3">
        <p className="text-[11px] uppercase tracking-wide text-ink-muted">
          Ajouter un compte Capital.com
        </p>
        {addable.length === 0 ? (
          <p className="text-[11px] leading-snug text-ink-muted">
            {brokerAccounts.length === 0
              ? 'Aucune session Capital.com connectée : la liste des comptes est indisponible.'
              : 'Tous les comptes de la clé Capital.com sont déjà ajoutés.'}
          </p>
        ) : (
          <div className="flex flex-col gap-2 sm:flex-row">
            <select
              value={adding}
              onChange={(event) => setAdding(event.target.value)}
              className="num min-h-touch min-w-0 flex-1 rounded-md border border-border bg-elevated px-3 text-ink outline-none focus:border-accent"
            >
              <option value="">— choisir —</option>
              {addable.map((option) => (
                <option key={option.broker_account_id} value={option.broker_account_id}>
                  {option.name} · {option.balance ?? '—'} {option.currency}
                  {option.is_live ? ' · RÉEL' : ''}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={label}
              placeholder="Nom (facultatif)"
              maxLength={64}
              onChange={(event) => setLabel(event.target.value)}
              className="min-h-touch min-w-0 flex-1 rounded-md border border-border bg-elevated px-3 text-ink outline-none focus:border-accent"
            />
            <button
              type="button"
              disabled={!adding || busy}
              onClick={() => void add()}
              className="min-h-touch rounded-md bg-accent px-4 text-[13px] font-semibold text-white disabled:opacity-40"
            >
              Ajouter
            </button>
          </div>
        )}
      </div>
    </Card>
  )
}

function AccountRow({
  account,
  onChanged,
  onError,
  onNotice,
}: {
  account: TradingAccountAdmin
  onChanged: () => void
  onError: (message: string) => void
  onNotice: (message: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(account.label)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  async function rename() {
    setBusy(true)
    try {
      await api.renameTradingAccount(account.id, draft)
      setEditing(false)
      onChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Renommage refusé')
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    setBusy(true)
    try {
      await api.deleteTradingAccount(account.id)
      onNotice(`« ${account.display_name} » retiré de l'application.`)
      onChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Suppression refusée')
    } finally {
      setBusy(false)
      setConfirming(false)
    }
  }

  return (
    <li className="px-3 py-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-[14px] font-semibold text-ink-strong">
            <span
              aria-hidden
              className={`h-2 w-2 shrink-0 rounded-full ${
                account.connected ? 'bg-up' : account.error ? 'bg-down' : 'bg-ink-muted/60'
              }`}
            />
            <span className="truncate">{account.display_name}</span>
            {account.is_live && (
              <span className="rounded bg-down/20 px-1.5 py-0.5 text-[10px] font-bold text-down">
                RÉEL
              </span>
            )}
          </p>
          <p className="num text-[11px] text-ink-muted">
            {/* Le courtier, puis la technologie — seulement si elle diffère :
                « FTMO · cTrader », mais pas « Capital.com · Capital.com ». */}
            {[account.broker_name, account.provider === 'ctrader' ? 'cTrader' : 'Capital.com']
              .filter((part, index, parts) => part && parts.indexOf(part) === index)
              .join(' · ')}{' '}
            n° {account.external_id}
          </p>
          <p className="text-[11px] text-ink-muted">
            {account.usernames.length > 0
              ? `Ouvert à : ${account.usernames.join(', ')}`
              : "Ouvert à personne — seule l'administration peut le piloter"}
          </p>
          {account.error && !account.connected && (
            <p className="text-[11px] text-down">Liaison : {account.error}</p>
          )}
        </div>
        <div className="flex shrink-0 gap-1">
          <button
            type="button"
            onClick={() => {
              setDraft(account.label)
              setEditing((value) => !value)
            }}
            className="min-h-touch rounded border border-border px-2.5 text-[12px] text-ink-muted hover:bg-elevated/70"
          >
            Nommer
          </button>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="min-h-touch rounded border border-down/50 px-2.5 text-[12px] text-down active:bg-down/10"
          >
            Retirer
          </button>
        </div>
      </div>

      {editing && (
        <div className="mt-2 flex gap-2">
          <input
            type="text"
            value={draft}
            maxLength={64}
            placeholder="ex. FTMO 100k — phase 1"
            onChange={(event) => setDraft(event.target.value)}
            className="min-h-touch min-w-0 flex-1 rounded-md border border-border bg-elevated px-3 text-ink outline-none focus:border-accent"
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => void rename()}
            className="min-h-touch rounded-md bg-accent px-4 text-[13px] font-semibold text-white disabled:opacity-40"
          >
            OK
          </button>
        </div>
      )}

      {/*
        Retirer un compte le retire de l'APPLICATION, pas du courtier : les
        positions ouvertes y restent, et la surveillance du gardien s'arrête.
        Deux effets qu'on ne découvre pas après coup.
      */}
      {confirming && (
        <div className="mt-2 rounded-md border border-down/40 bg-down/10 p-2 text-[12px] text-ink">
          <p>
            Le compte reste intact chez le courtier, positions comprises, mais{' '}
            <strong>la coupure automatique cessera de le surveiller</strong>.
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void remove()}
              className="min-h-touch flex-1 rounded bg-down text-[13px] font-semibold text-white disabled:opacity-50"
            >
              {busy ? '…' : 'Retirer'}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="min-h-touch flex-1 rounded bg-elevated text-[13px] text-ink-muted"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </li>
  )
}

// --------------------------------------------------------------------------- //
// cTrader
// --------------------------------------------------------------------------- //
function CTraderCard({
  status,
  accounts,
  onChanged,
  onError,
  onNotice,
}: {
  status: CTraderStatus
  accounts: TradingAccountAdmin[]
  onChanged: () => void
  onError: (message: string) => void
  onNotice: (message: string) => void
}) {
  const [editingApp, setEditingApp] = useState(!status.app_configured)
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  async function saveApp() {
    setBusy(true)
    try {
      await api.saveCtraderApp({ client_id: clientId, client_secret: clientSecret })
      setClientId('')
      setClientSecret('')
      setEditingApp(false)
      onNotice('Clé de l’application cTrader enregistrée, chiffrée sur le serveur.')
      onChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Enregistrement refusé')
    } finally {
      setBusy(false)
    }
  }

  async function connect() {
    setBusy(true)
    try {
      const { url } = await api.ctraderAuthorize()
      // Navigation complète, et non une fenêtre : la page cTrader renvoie
      // ensuite ici, avec le résultat dans l'adresse.
      window.location.assign(url)
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Raccordement impossible')
      setBusy(false)
    }
  }

  async function disconnect(id: number) {
    setBusy(true)
    try {
      await api.deleteCtraderConnection(id)
      onNotice('cTrader ID déconnecté. Ses comptes restent listés, sans liaison.')
      onChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Déconnexion refusée')
    } finally {
      setBusy(false)
    }
  }

  async function copyRedirect() {
    try {
      await navigator.clipboard.writeText(status.redirect_uri)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      /* Presse-papiers indisponible : l'adresse reste sélectionnable. */
    }
  }

  const byId = new Map(accounts.map((account) => [account.id, account]))

  return (
    <Card title="cTrader — FTMO et prop firms">
      <div className="flex flex-col gap-3 p-3">
        {/* 1. La clé de l'application « Reverso » */}
        <div>
          <p className="mb-1 text-[11px] uppercase tracking-wide text-ink-muted">
            1. Clé de l'application
          </p>
          {editingApp ? (
            <div className="flex flex-col gap-2">
              <p className="text-[11px] leading-snug text-ink-muted">
                Sur openapi.ctrader.com, bouton <strong className="text-ink">Credentials</strong>{' '}
                de votre application. Elle est chiffrée sur le serveur et n'est plus jamais
                réaffichée.
              </p>
              <input
                type="text"
                value={clientId}
                placeholder="Client ID"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                onChange={(event) => setClientId(event.target.value)}
                className="num min-h-touch rounded-md border border-border bg-elevated px-3 text-ink outline-none focus:border-accent"
              />
              <input
                type="password"
                value={clientSecret}
                placeholder="Client Secret"
                autoComplete="off"
                onChange={(event) => setClientSecret(event.target.value)}
                className="num min-h-touch rounded-md border border-border bg-elevated px-3 text-ink outline-none focus:border-accent"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={busy || !clientId.trim() || !clientSecret.trim()}
                  onClick={() => void saveApp()}
                  className="min-h-touch flex-1 rounded-md bg-accent text-[13px] font-semibold text-white disabled:opacity-40"
                >
                  Enregistrer
                </button>
                {status.app_configured && (
                  <button
                    type="button"
                    onClick={() => setEditingApp(false)}
                    className="min-h-touch rounded-md border border-border bg-elevated px-4 text-[13px] text-ink-muted"
                  >
                    Annuler
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <p className="num text-[13px] text-up">
                ✓ Enregistrée <span className="text-ink-muted">({status.client_id_hint})</span>
              </p>
              <button
                type="button"
                onClick={() => setEditingApp(true)}
                className="min-h-touch rounded border border-border px-3 text-[12px] text-ink-muted"
              >
                Remplacer
              </button>
            </div>
          )}
        </div>

        {/* 2. L'adresse de retour à déclarer chez Spotware */}
        <div>
          <p className="mb-1 text-[11px] uppercase tracking-wide text-ink-muted">
            2. Adresse de retour à déclarer
          </p>
          <p className="mb-1 text-[11px] leading-snug text-ink-muted">
            Sur openapi.ctrader.com, bouton <strong className="text-ink">Edit</strong> de votre
            application : ajoutez cette adresse aux « Redirect URIs », à l'identique.
          </p>
          <div className="flex gap-2">
            <code className="num min-w-0 flex-1 select-all overflow-x-auto whitespace-nowrap rounded-md border border-border bg-elevated px-2 py-2.5 text-[12px] text-ink">
              {status.redirect_uri}
            </code>
            <button
              type="button"
              onClick={() => void copyRedirect()}
              className="min-h-touch shrink-0 rounded-md border border-border bg-elevated px-3 text-[12px] text-ink"
            >
              {copied ? 'Copié ✓' : 'Copier'}
            </button>
          </div>
        </div>

        {/* 3. Raccorder un cTrader ID */}
        <div>
          <p className="mb-1 text-[11px] uppercase tracking-wide text-ink-muted">
            3. Raccorder un cTrader ID
          </p>
          <p className="mb-2 text-[11px] leading-snug text-ink-muted">
            Vous serez envoyé sur la page officielle de cTrader : connectez-vous, autorisez
            l'application. Tous les comptes de ce cTrader ID — FTMO, une autre prop firm —
            arriveront ici.
          </p>
          <button
            type="button"
            disabled={busy || !status.app_configured}
            onClick={() => void connect()}
            className="min-h-touch w-full rounded-md bg-accent text-[14px] font-semibold text-white disabled:opacity-40"
          >
            {status.app_configured ? 'Raccorder un cTrader ID' : "Enregistrez d'abord la clé"}
          </button>
        </div>

        {status.connections.length > 0 && (
          <div>
            <p className="mb-1 text-[11px] uppercase tracking-wide text-ink-muted">
              cTrader ID raccordés
            </p>
            <ul className="flex flex-col gap-2">
              {status.connections.map((connection) => (
                <li
                  key={connection.id}
                  className="flex items-start justify-between gap-2 rounded-md border border-border bg-elevated/40 p-2"
                >
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-ink-strong">{connection.label}</p>
                    <p className="text-[11px] text-ink-muted">
                      {connection.account_ids
                        .map((id) => byId.get(id)?.display_name ?? `compte ${id}`)
                        .join(' · ') || 'aucun compte'}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void disconnect(connection.id)}
                    className="min-h-touch shrink-0 rounded border border-down/50 px-2.5 text-[12px] text-down disabled:opacity-40"
                  >
                    Déconnecter
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/*
          Rappel des règles des prop firms, vérifiées pour FTMO : personne
          d'autre que le titulaire ne doit trader le compte. L'application
          sait ouvrir un compte à un trader ; la règle, elle, reste la vôtre.
        */}
        <p className="rounded-md border border-warn/40 bg-warn/10 p-2 text-[11px] leading-relaxed text-ink-muted">
          Les prop firms interdisent en général qu'un tiers trade le compte : chez FTMO,
          seul le titulaire peut l'utiliser. Ouvrir un compte de prop firm à un autre trader
          peut faire annuler le challenge.
        </p>
      </div>
    </Card>
  )
}
